import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
type ColumnType = 'text' | 'number' | 'select' | 'checkbox' | 'date' | 'radio' | 'signature' | 'time';

export interface SelectOption {
  value: string | number
  label: string
}
export interface TableColumn {
  id: string;
  name: string;
  type: ColumnType;
  options?: string[];
  required?: boolean;
  complianceRules?: ComplianceRule[];
}

export interface ComplianceRule {
  id: string;
  severity: 'Critical' | 'Major' | 'Minor' | 'Informational';
  description: string;
  nonCompliantValues: string[]; // option values that trigger non-compliance
}

export interface FormField {
  description?: any; 
  id: string
  type: string | "webcam";
  tableOptions?: {
    columns?: TableColumn[];
    initialData?: Record<string, any>[];
    data?: Record<string, any>[];
    allowColumnEdit?: boolean;
  };
  label: string
  placeholder?: string
  required?: boolean
  options?: (string | SelectOption)[]
  helpText?: string
  validation?: {
    pattern?: string
    minLength?: number
    maxLength?: number
    customValidation?: string
    min?: number
    max?: number
  }
  min?: number
  max?: number
  defaultValue?: string | number
  width?: number
  height?: number
  backgroundColor?: string
  penColor?: string
  minDate?: string
  maxDate?: string
  interval?: number
  accept?: string
  multiple?: boolean
  maxSize?: number
  defaultColor?: string
  step?: number
  pattern?: string
  minLength?: number
  maxLength?: number
  displayOptions?: {
    horizontal?: boolean
    inline?: boolean
  }
  minSelect?: number
  maxSelect?: number
  layout?: 'vertical' | 'horizontal'
  size?: 'small' | 'medium' | 'large'
  fontStyle?: {
    fontSize: string
    fontWeight: string
    fontFamily: string
    color: string
    textAlign: string
    lineHeight: string
    letterSpacing: string
    textDecoration: string
    textTransform: string
  }
  colSpan?: number
  webcamOptions?: {
    resolution?: string // e.g., "640x480", "1280x720"
    facingMode?: "user" | "environment"
    allowFlip?: boolean
  }
  complianceRules?: ComplianceRule[];
}

export interface FormConfig {
  id: string
  name: string
  fields: FormField[]
  submissionId?: string
  submissionData?: Record<string, any>
  isReadOnly?: boolean
  createdAt?: string
  updatedAt?: string
  reportName?: string
  json?: string
  fieldJson?: string
  // changeSummary?: string
  schemaVersion?: string
  auditReportJson?: string
  addedBy?: string
  addedDate?: string
  updatedBy?: string
  updatedDate?: string
  inActive?: boolean
  inActiveReason?: string
  inActiveBy?: string
  inActiveDate?: string
  farmId?: any
}

interface FormBuilderState {
  forms: FormConfig[]
  currentForm: FormConfig | null
}

const initialState: FormBuilderState = {
  forms: [],
  currentForm: null,
}

const formBuilderSlice = createSlice({
  name: "formBuilder",
  initialState,
  reducers: {
    addForm: (state, action: PayloadAction<{ name: string }>) => {
      const newForm: FormConfig = {
        id: Date.now().toString(),
        name: action.payload.name,
        fields: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      state.forms.push(newForm)
    },
    updateForm: (state, action: PayloadAction<FormConfig>) => {
      const index = state.forms.findIndex((form) => form.id === action.payload.id)
      if (index !== -1) {
        state.forms[index] = {
          ...action.payload,
          updatedAt: new Date().toISOString(),
        }
      }
    },
    deleteForm: (state, action: PayloadAction<string>) => {
      state.forms = state.forms.filter((form) => form.id !== action.payload)
    },
    setCurrentForm: (state, action: PayloadAction<FormConfig | null>) => {
      state.currentForm = action.payload
    },
  },
})

export const { addForm, updateForm, deleteForm, setCurrentForm } = formBuilderSlice.actions
export default formBuilderSlice.reducer
